import React, { lazy, Suspense, useEffect, useRef, useState } from "react";
import { openAivioChat, streamChat } from "./chat/chatApi";
import { formatReply } from "./chat/formatReply";

const ThreeWorkflowScene = lazy(() => import("./ThreeWorkflowScene"));

// Only the India Budget agent is connected in this public demo; other use cases show a simulated run.
const LIVE_SLUG = "budget-knowledge";
const SIM_STEP_MS = 1100;

function AivioExperienceDemo({ useCase }) {
  const isLive = useCase.slug === LIVE_SLUG;
  const [query, setQuery] = useState("");
  const [response, setResponse] = useState("");
  const [status, setStatus] = useState("Ready when you are");
  const [active, setActive] = useState(false);
  const [submittedQuery, setSubmittedQuery] = useState("");
  const [simStep, setSimStep] = useState(-1);
  const threadRef = useRef(null);
  const abortRef = useRef(null);
  const simTimer = useRef(null);
  const bodyRef = useRef(null);
  const hasResponse = Boolean(response);

  useEffect(() => () => {
    abortRef.current?.abort();
    window.clearTimeout(simTimer.current);
  }, []);

  // Follow the simulated run step by step; for a live answer, bring its start into view once.
  useEffect(() => {
    const body = bodyRef.current;
    if (body && simStep >= 0) body.scrollTo({ top: body.scrollHeight, behavior: "smooth" });
  }, [simStep]);

  useEffect(() => {
    const body = bodyRef.current;
    const answer = body?.querySelector(".live-demo-response");
    if (body && answer && hasResponse) {
      const top = answer.getBoundingClientRect().top - body.getBoundingClientRect().top + body.scrollTop - 12;
      body.scrollTo({ top, behavior: "smooth" });
    }
  }, [hasResponse]);

  // Coordinator, then each specialist agent, then the deliverable.
  const simulate = (message) => {
    window.clearTimeout(simTimer.current);
    setSubmittedQuery(message);
    setActive(true);
    setStatus("Aivio’s agents are working through the request…");
    const last = useCase.agentTeam.length + 1;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      setSimStep(last);
      setActive(false);
      setStatus("Simulated run complete");
      return;
    }
    const advance = (step) => {
      setSimStep(step);
      if (step < last) {
        simTimer.current = window.setTimeout(() => advance(step + 1), SIM_STEP_MS);
      } else {
        setActive(false);
        setStatus("Simulated run complete");
      }
    };
    advance(0);
  };

  const askAivio = async (event, suggestedQuery) => {
    if (event) event.preventDefault();
    const message = (suggestedQuery || query).trim();
    if (!message || active) return;
    if (!isLive) {
      simulate(message);
      return;
    }

    setQuery(message);
    setSubmittedQuery(message);
    setResponse("");
    setStatus("Aivio is working through your request…");
    setActive(true);
    const controller = new AbortController();
    abortRef.current = controller;
    let text = "";

    try {
      await streamChat({
        message,
        threadId: threadRef.current,
        signal: controller.signal,
        onEvent: (item) => {
          if (item.thread_id) threadRef.current = item.thread_id;
          if (item.status && !text) setStatus(`${item.status}`);
          if (item.replace) text = item.replace;
          if (item.t) text += item.t;
          if (item.t || item.replace) setResponse(text);
        },
      });
      setStatus(text ? "Aivio responded" : "Aivio didn’t return an answer. Please try again.");
    } catch (error) {
      if (error.name !== "AbortError") setStatus("Could not reach Aivio. Please try again in a moment.");
    } finally {
      setActive(false);
    }
  };

  return (
    <section className="aivio-live-demo-section" id="try-aivio" style={{ "--case-accent": useCase.accent }}>
      <div className="container">
        <div className="live-demo-heading">
          <div><div className="eyebrow section-eyebrow">{isLive ? "TRY AIVIO · LIVE INTERACTIVE DEMO" : "TRY AIVIO · INTERACTIVE PREVIEW"}</div><h2>Explore the workflow. <span>{isLive ? "Ask Aivio." : "Watch the agents work."}</span></h2></div>
          <p>{isLive ? "Send a real request to the Aivio agent. The workflow animation speeds up while your request is being handled." : "Choose a sample request to see how Aivio’s agents would handle it in your environment. For a live conversation, try the India Budget agent."}</p>
        </div>
        <div className="live-demo-layout">
          <div className="live-demo-visual-card">
            <div className="live-demo-visual-head"><span><i className={active ? "live-status-dot is-active" : "live-status-dot"} />{active ? "AGENTS WORKING" : "AGENT WORKFLOW"}</span><small>BUILT ON IBM watsonx Orchestrate</small></div>
            <Suspense fallback={<div className="three-workflow-scene three-workflow-loading"><div className="three-fallback-nodes"><span>Business data</span><b>→</b><span>Aivio agents</span><b>→</b><span>Systems</span></div></div>}><ThreeWorkflowScene active={active} /></Suspense>
            <div className="live-demo-legend"><span><i className="legend-source" />Business data</span><b>→</b><span><i className="legend-agent" />Specialist agents</span><b>→</b><span><i className="legend-system" />Connected systems</span></div>
            <div className="live-demo-visual-foot"><span className="live-demo-orbit">✦</span><p>Aivio coordinates focused tasks, carries context forward and prepares the next step for people or business systems.</p></div>
          </div>

          <div className="live-demo-chat-card">
            <div className="live-demo-chat-head"><div className="live-demo-avatar">✦</div><div><strong>Aivio</strong><small>{isLive ? "Live agent" : "Simulated run"} · {useCase.title}</small></div><span className={`live-demo-online${isLive ? "" : " is-preview"}`}><i />{isLive ? "CONNECTED" : "PREVIEW"}</span></div>
            <div className="live-demo-chat-body" aria-live="polite" ref={bodyRef}>
              <div className="live-demo-assistant-message"><span>AIVIO</span><p>{isLive ? <>Hi, I’m Aivio. Try a request for <b>{useCase.title}</b>, or choose one of the examples below.</> : <>Hi, I’m Aivio. Choose an example below to see how the <b>{useCase.title}</b> agents would work through it.</>}</p></div>
              {submittedQuery && <div className="live-demo-user-message">{submittedQuery}</div>}
              {isLive && active && <div className="live-demo-thinking"><span /><span /><span /> Aivio is coordinating the next steps</div>}
              {isLive && response && <div className="live-demo-response"><div><span>AIVIO RESPONSE</span><span className="response-source-mark">✦</span></div><div className="live-demo-response-body" dangerouslySetInnerHTML={{ __html: formatReply(response) }} /></div>}
              {!isLive && simStep >= 0 && (
                <div className="live-demo-response">
                  <div><span>AGENT RUN · SIMULATED</span><span className="response-source-mark">✦</span></div>
                  <ol className="live-demo-steps">
                    {[{ name: "Coordinator agent", description: "Understands the request and delegates focused tasks." }, ...useCase.agentTeam].map((agent, index) => (
                      <li key={agent.name} className={simStep > index ? "is-done" : simStep === index ? "is-working" : ""}>
                        <i aria-hidden="true">{simStep > index ? "✓" : ""}</i><div><b>{agent.name}</b><small>{agent.description}</small></div>
                      </li>
                    ))}
                  </ol>
                  {simStep > useCase.agentTeam.length && <p className="live-demo-deliverable"><b>Result for review:</b> {useCase.deliverable}</p>}
                </div>
              )}
            </div>
            <div className="live-demo-suggestions"><span>TRY A SAMPLE REQUEST</span><div>{useCase.demoPrompts.map((prompt) => <button type="button" key={prompt} onClick={() => askAivio(null, prompt)} disabled={active}>{prompt}</button>)}</div></div>
            {isLive ? (
              <form className="live-demo-input" onSubmit={askAivio}><label className="visually-hidden" htmlFor="aivio-live-prompt">Ask Aivio</label><input id="aivio-live-prompt" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Ask about this workflow…" disabled={active} /><button type="submit" aria-label="Send request to Aivio" disabled={active || !query.trim()}>{active ? <span className="send-spinner" /> : "↑"}</button></form>
            ) : (
              <div className="live-demo-preview-actions"><button type="button" onClick={() => openAivioChat()}>Chat with the live India Budget agent <span aria-hidden="true">→</span></button><a href="https://www.c-metric.com/contact/" target="_blank" rel="noopener noreferrer">Book a demo for this workflow</a></div>
            )}
            <div className="live-demo-status">{status}<span>{isLive ? " · Live response from the Aivio agent" : " · Simulated: no business systems are connected"}</span></div>
          </div>
        </div>
        <p className="live-demo-disclaimer">Responses depend on the connected agent, available knowledge and configured business actions. This interactive preview does not execute transactions in your systems.</p>
      </div>
    </section>
  );
}

export default AivioExperienceDemo;
